import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { collectForegroundActivity } from "~/api/activity/foregroundActivity";
import { ImpersonationBanner } from "~/components/ImpersonationBanner";
import { AuthView } from "~/components/auth/AuthView";
import { EmptyLayout } from "~/components/layout/EmptyLayout";
import { NavigationLayout } from "~/components/layout/NavigationLayout";
import { toast } from "~/components/ui/Toaster";
import { useAuth } from "~/hooks/useAuth";
import { useCheckoutSessionStatus } from "~/hooks/useCheckoutSessionStatus";
import { useOnMount } from "~/hooks/useOnMount";
import { PaymentSuccessDialog } from "~/routes/(restricted)/byggeradar/-components/PaymentSuccessDialog";

const WaitForAuth = () => <div className="size-full bg-white"></div>;

export const Route = createFileRoute("/(restricted)")({
  pendingMs: 0,
  pendingComponent: WaitForAuth,
  component: RouteComponent,
  loader: async ({ location, context: { i18n } }) => {
    // The subscription upgrade flow mounts UpdatePaymentMethodModal mid-update.
    // Suspending on a lazily-loaded namespace there re-suspends this route's
    // boundary on every reveal, which loops until React's update-depth limit.
    await i18n.loadNamespaces("profile");

    return {
      showEmailVerifiedNotice: location.state.emailVerified,
    };
  },
});

function RouteComponent() {
  const { showEmailVerifiedNotice } = Route.useLoaderData();

  const navigate = useNavigate();
  const { session_id } = useSearch({ strict: false });

  const { user, authClient } = useAuth();

  useEffect(() => collectForegroundActivity(authClient), [authClient]);

  const { t } = useTranslation(["auth"]);

  const sessionStatus = useCheckoutSessionStatus(session_id);

  const handlePaymentDialogClose = () => {
    void navigate({
      to: ".",
      search: (prev: Partial<{ session_id: string }>) => {
        const { session_id: _, ...rest } = prev;
        return rest;
      },
      replace: true,
      viewTransition: false,
      ignoreBlocker: true,
    });
  };

  const matchRoute = useMatchRoute();
  const isReportView = !!matchRoute({ to: "/rapporter/$reportSlug" });

  const CurrentLayout = useMemo(
    () => (isReportView ? EmptyLayout : NavigationLayout),
    [isReportView],
  );

  useOnMount(() => {
    if (showEmailVerifiedNotice) {
      toast(t("auth:email_verification.email_verified"));
    }
  });

  return !user?.isAuthenticated ? (
    <AuthView />
  ) : (
    <div className="flex h-screen w-full flex-col">
      <div className="min-h-0 flex-1">
        <CurrentLayout>
          <Outlet />
          <PaymentSuccessDialog
            isOpen={sessionStatus?.status === "complete"}
            onClose={handlePaymentDialogClose}
          />
        </CurrentLayout>
      </div>
      <ImpersonationBanner />
    </div>
  );
}
